import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject, OnInit } from '@angular/core';
import { Client, HlrInterface, Product, RequestLimits, ResponseFields } from '@helpers/types/models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AlarisEditPanelService, CustomValidators, EditPanelInputData } from '@campaign-portal/components-library';
import { ProductsService } from '@helpers/repo/products.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { ClientsService } from '../clients.service';
import { debounceTime } from 'rxjs';
import { eapiEnable } from '../../../app.module';
import { AdditionalValidators, NetworkValidators } from '@helpers/shared/validators';
import moment from 'moment';

@Component({
	selector: 'app-edit-client',
	templateUrl: './edit-client.component.html',
	styleUrl: './edit-client.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class EditClientComponent implements OnInit {
	readonly errors: { key: string; value: string }[] = [
		{key: 'mask', value: 'gl.errors.invalid_format'},
		{key: 'required', value: 'gl.errors.required'}];
	readonly client: Client = {
		id: null,
		login: null,
		password: '',
		productId: '',
		availableInterfaces: ['http'],
		removeLeadingZeroFromMnc: false,
		maxSessionsAmount: 1000,
		enumAddressList: [],
		enumLoginAuth: false,
		fieldsOfResponse: [],
		allowDebug: false,
		forceResultToOne: false,
		requestLimits: {
			periodType: '',
			periodNumber: null,
			limit: null,
			effectiveFrom: moment().toJSON()
		}
	};
	readonly clientForm = new FormGroup({
		login: new FormControl<string | null>(null, { validators: Validators.required }),
		password: new FormControl('', { nonNullable: true, validators: Validators.required }),
		productId: new FormControl('', { validators: Validators.pattern('^\\d+$') }),
		availableInterfaces: new FormControl<HlrInterface[]>([], CustomValidators.requiredArrayOrNull),
		removeLeadingZeroFromMnc: new FormControl(false, { nonNullable: true }),
		maxSessionsAmount: new FormControl(
			1000,
			{
				nonNullable: true,
				validators: [
					Validators.required,
					Validators.pattern('^\\d+$'),
					Validators.min(1),
					Validators.max(10000)
				]
			}),
		enumAddressList: new FormControl<string[]>(
			{ disabled: true, value: [] },
			{
				nonNullable: true,
				validators: [
					Validators.required,
					CustomValidators.requiredArrayOrNull,
					NetworkValidators.arrayOfIpv4Validator,
					AdditionalValidators.uniqueArray
				]
			}
		),
		enumLoginAuth: new FormControl(false, { nonNullable: true }),
		fieldsOfResponse: new FormControl<string[]>(
			{ disabled: true, value: [] },
			CustomValidators.requiredArrayOrNull
		),
		allowDebug: new FormControl(false),
		forceResultToOne: new FormControl(false),
		requestLimits: new FormGroup({
			periodType: new FormControl<"" | "hour" | "day" | "week">(
				'',
				{ nonNullable: true },
			),
			periodNumber: new FormControl<number | null>(null, [
				Validators.pattern('^\\d+$'),
				Validators.min(1),
				Validators.max(500)
			]),
			limit: new FormControl<number | null>(null, [
				Validators.pattern('^\\d+$'),
				Validators.min(1),
				Validators.max(100000000)
			]),
			effectiveFrom: new FormControl<string>('', { nonNullable: true }),
			requestLimitsCheckbox: new FormControl<boolean>(false, { nonNullable: true })
		}, {
			validators: [
				AdditionalValidators.requiredIfOtherHasValue('limit', 'periodType'),
				AdditionalValidators.requiredIfOtherHasValue('periodType', 'periodNumber')
			]
		}),
	});

	readonly fields: string[] = [];
	readonly interfaces: HlrInterface[] = ['http', 'enum', 'smsrt'];
	products: Product[] = [];
	readonly productsFilter = new FormControl('');
	readonly separator = ', ';
	readonly noneProduct = {
		product_id: '',
		product_caption: '',
		acc_currency_code: '',
		product_direction: 0,
		product_type: 0
	} as unknown as Product;
	private readonly destroyRef = inject(DestroyRef);

	constructor(
		@Inject(EditPanelInputData) private readonly inputData: { client?: Client },
		public readonly clientsService: ClientsService,
		public readonly productsService: ProductsService,
		private readonly editPanel: AlarisEditPanelService
	) {
		if ( this.inputData.client ) {
			this.client = this.inputData.client;
		}
	}

	ngOnInit(): void {
		this.clientsService.getResponseFields()
			.pipe((takeUntilDestroyed(this.destroyRef)))
			.subscribe(fields => {
				if (this.client.fieldsOfResponse.length > 0) {
					this.client.fieldsOfResponse = this.getMatchingEnumNames(fields.responseFields,
						this.client.fieldsOfResponse);
					this.clientForm.controls.fieldsOfResponse.setValue(this.client.fieldsOfResponse);
				}
				fields.responseFields?.forEach(field => {
					this.fields.push(field.enumName);
					if (field.isDefaultSelected && this.client.fieldsOfResponse.length === 0) {
						this.client.fieldsOfResponse.push(field.enumName);
					}
				})
				if (!this.client.id) {
					this.clientForm.controls.fieldsOfResponse.setValue(this.client.fieldsOfResponse);
				}
			})
		this.productsService.clientProducts$
			.pipe((takeUntilDestroyed(this.destroyRef)))
			.subscribe((list) => {
				this.productsFilter.reset();
				this.products = [this.noneProduct, ...list];
			});

		this.productsFilter.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(150))
			.subscribe((value) => {
				this.products = [
					this.noneProduct,
					...this.productsService.clientProducts.filter((product) => {
						return product.product_caption.toLowerCase().includes(value?.toLowerCase() ?? '');
					})
				];
			});
		this.clientForm.controls.enumLoginAuth.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				if (value) {
					this.clientForm.controls.login.addValidators(Validators.required);
				}
				if (!value && this.clientForm.controls.availableInterfaces.value?.at(0) === 'enum'
					&& this.clientForm.controls.availableInterfaces.value.length === 1) {
					this.clientForm.controls.login.removeValidators(Validators.required);
				}
				this.clientForm.controls.login.updateValueAndValidity();
			})
		this.clientForm.controls.availableInterfaces.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				if ( value === null ) {
					this.clientForm.controls.fieldsOfResponse.enable();
					this.clientForm.controls.enumAddressList.enable();
				} else {
					const httpType = value.includes('http');
					const enumType = value.includes('enum');
					const smsrtType = value.includes('smsrt');

					if (httpType || smsrtType) {
						this.clientForm.controls.login.addValidators(Validators.required);
						this.clientForm.controls.password.addValidators(Validators.required);
					} else {
						this.clientForm.controls.login.removeValidators(Validators.required);
						this.clientForm.controls.password.removeValidators(Validators.required);
					}

					if (!enumType) {
						this.clientForm.controls.enumLoginAuth.reset();
					}

					this.clientForm.controls.login.updateValueAndValidity();
					this.clientForm.controls.password.updateValueAndValidity();

					this.clientForm.controls.fieldsOfResponse[httpType ? 'enable' : 'disable']();
					this.clientForm.controls.fieldsOfResponse
						.patchValue(httpType ? this.clientForm.controls.fieldsOfResponse.value : []);
					this.clientForm.controls.enumAddressList[enumType ? 'enable' : 'disable']();
					this.clientForm.controls.enumAddressList
						.patchValue(enumType ? this.clientForm.controls.enumAddressList.value : []);
				}
			});
		this.clientForm.controls.requestLimits.valueChanges
			.pipe((takeUntilDestroyed(this.destroyRef)))
			.subscribe(value => {
				if (!value.requestLimitsCheckbox) {
					this.clientForm.controls.requestLimits.controls.limit.reset(null, {emitEvent: false});
				}
				this.clientForm.updateValueAndValidity();
			})
		this.reset();
	}

	displayProduct: (id: Id<exist>) => string = (id: Id<exist>): string => {
		const product = this.productsService.map.get(+id);
		if ( product ) {
			return `${product.product_caption} (${product.acc_currency_code}) (${product.product_id})`;
		}
		return '';
	};

	reset(): void {
		const client = structuredClone(this.client);
		const value = {
			...client,
			requestLimits: client.requestLimits?.limit ? {
				...client.requestLimits,
				effectiveFrom: moment(client.requestLimits.effectiveFrom, 'YYYY:MM:DD HH:mm:ss').toJSON(),
				requestLimitsCheckbox: !!client.requestLimits.limit
			} : this.defaultRequestLimits()
		}
		this.clientForm.reset(value);
	}

	defaultRequestLimits = (): RequestLimits => {
		return {
			periodType: '' as 'hour' | 'day' | 'week' | '',
			periodNumber: null,
			limit: null,
			effectiveFrom: moment().toJSON(),
			requestLimitsCheckbox: false
		}
	}

	getMatchingEnumNames(
		data: ResponseFields[],
		valuesToMatch: string[]
	): string[] {
		const result: string[] = [];

		data.forEach(item => {
			if (
				valuesToMatch.includes(item.nameV1 ?? '') ||
				valuesToMatch.includes(item.nameV2 ?? '') ||
				valuesToMatch.includes(item.enumName ?? '')
			) {
				result.push(item.enumName);
			}
		});
		return result;
	}

	save(): void {
		const client: Client = {
			id: this.client.id,
			login: this.clientForm.controls.login.value,
			password: this.clientForm.controls.password.value,
			productId: String(this.clientForm.controls.productId.value ?? ''),
			availableInterfaces: this.clientForm.controls.availableInterfaces.value === null
				? this.interfaces
				: this.clientForm.controls.availableInterfaces.value,
			removeLeadingZeroFromMnc: this.clientForm.controls.removeLeadingZeroFromMnc.value,
			maxSessionsAmount: +this.clientForm.controls.maxSessionsAmount.value,
			enumAddressList: this.clientForm.controls.enumAddressList.value,
			enumLoginAuth: this.clientForm.controls.enumLoginAuth.value,
			fieldsOfResponse: this.clientForm.controls.fieldsOfResponse.value === null
				? this.fields
				: this.clientForm.controls.fieldsOfResponse.value ?? [],
			allowDebug: this.clientForm.controls.allowDebug.value,
			forceResultToOne: this.clientForm.controls.forceResultToOne.value,
			requestLimits: {
				periodType: this.clientForm.controls.requestLimits.controls.periodType.value,
				periodNumber: Number(this.clientForm.controls.requestLimits.controls.periodNumber.value),
				limit: Number(this.clientForm.controls.requestLimits.controls.limit.value),
				effectiveFrom: moment(this.clientForm.controls.requestLimits.controls.effectiveFrom.value)
					.format('YYYY:MM:DD HH:mm:ss')
			}
		};
		if (!client?.requestLimits?.limit) {
			client.requestLimits = null
		}
		this.clientsService.update(client)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp) => {
				if ( resp.success || resp.createdId) {
					this.editPanel.close(true);
				}
			});
	}

	close(): void {
		this.editPanel.close(false);
	}

	generatePassword(): void {
		this.clientForm.get('password')?.setValue(Math.random().toString(36).slice(-8));
	}

	protected readonly eapiEnable = eapiEnable;
}
